import yaml from 'js-yaml';
import { NODE_KIND_META } from '@/theme/tokens';
import {
  edgeId,
  isControlEdge,
  type FlowDefinition,
  type FlowEdge,
  type FlowNode,
} from './types';

export interface Diagnostic {
  severity: 'error' | 'warning' | 'info';
  /** Compiler-style code where one exists; preview codes prefixed OL-PRE-*. */
  code: string;
  message: string;
  nodeId?: string;
  edgeId?: string;
}

export class FlowParseError extends Error {}

/** Parse a Flow IR YAML document into a typed definition. Throws FlowParseError on hard failures. */
export function parseFlowYaml(source: string): FlowDefinition {
  let doc: unknown;
  try {
    doc = yaml.load(source);
  } catch (e) {
    throw new FlowParseError(`YAML parse failed: ${(e as Error).message}`);
  }
  return parseFlowObject(doc);
}

/**
 * Validate an ALREADY-PARSED Flow IR document (the wire delivers canonical IR as a JSON object —
 * e.g. TemplateDetail.irPreview) into a typed definition. Throws FlowParseError on hard failures.
 */
export function parseFlowObject(doc: unknown): FlowDefinition {
  if (!doc || typeof doc !== 'object') {
    throw new FlowParseError('Flow IR document is empty or not an object.');
  }
  const def = doc as FlowDefinition;
  if (def.schema !== 'flow-definition.v1') {
    throw new FlowParseError(`Unexpected schema discriminator: ${String(def.schema)}`);
  }
  if (!def.id || typeof def.version !== 'number' || !def.metadata?.name) {
    throw new FlowParseError('Flow IR missing required id / version / metadata.name.');
  }
  return def;
}

const PLAIN_OVER = /^nodes(?:\.[A-Za-z0-9_]+|\['[^']+'\])\.[A-Za-z0-9_]+(?:\.[A-Za-z0-9_]+)*$/;

/**
 * The fan-out rules as you edit, with the compiler's codes (OL-IR-110 to 116). The compiler stays the
 * authority: template lookup and recursion are only checked on Validate.
 */
export function fanOutDiagnostics(def: FlowDefinition): Diagnostic[] {
  const out: Diagnostic[] = [];
  const nodes = def.nodes ?? [];
  const edges = def.edges ?? [];
  const byId = new Map(nodes.map((n) => [n.id, n]));
  for (const n of nodes) {
    if (n.kind !== 'dynamicBranch') continue;
    const say = (severity: Diagnostic['severity'], code: string, message: string) =>
      out.push({ severity, code, nodeId: n.id, message });
    const g = n.guardrails ?? {};
    const missing = [
      !n.over?.trim() && 'the list to fan out over',
      !n.flow?.id?.trim() && 'the flow to run per item',
      !n.entityBinding?.trim() && 'the entity role',
      !n.policy && 'when to finish',
      g.maxFanOut == null && 'maximum children',
      g.maxDepth == null && 'maximum nesting',
      g.budget == null && 'budget',
      !g.onLimit && 'what to do when a limit is reached',
    ].filter(Boolean);
    if (missing.length) say('error', 'OL-IR-110', `This fan-out still needs ${missing.join(', ')}. Limits have no defaults.`);
    if (n.over?.trim() && !PLAIN_OVER.test(n.over.trim())) {
      say('warning', 'OL-IR-116', 'The list is not a plain path such as nodes.registry.directors, so a child cannot find its item in the evidence and its capability steps will fail.');
    }
    if (g.maxFanOut != null && (g.maxFanOut < 1 || g.maxFanOut > 500)) say('error', 'OL-IR-001', 'Maximum children must be 1 to 500.');
    if (g.maxDepth != null && (g.maxDepth < 1 || g.maxDepth > 10)) say('error', 'OL-IR-001', 'Maximum nesting must be 1 to 10.');
    if (g.budget != null && g.budget < 1) say('error', 'OL-IR-001', 'Budget must be at least 1.');
    if (n.policy === 'QUORUM') {
      if (n.quorum == null) say('error', 'OL-IR-115', 'Finishing on QUORUM needs a quorum.');
      else if (n.quorum < 1 || (g.maxFanOut != null && n.quorum > g.maxFanOut)) say('error', 'OL-IR-115', 'The quorum must be between 1 and the maximum children.');
    } else if (n.quorum != null) {
      say('error', 'OL-IR-115', 'A quorum only applies when finishing on QUORUM.');
    }
    const listed = n.successOutcomes ?? [];
    if (listed.some((o) => !o.trim()) || new Set(listed).size !== listed.length) {
      say('error', 'OL-IR-001', 'Outcomes that count as success must each be different and not blank.');
    }
    const routes = (status: string) =>
      edges.filter((e) => e.from === n.id && e.kind === 'status' && e.onStatus === status).map((e) => e.to);
    for (const [status, meaning] of [['EMPTY', 'an empty list'], ['LIMIT_REACHED', 'a reached limit']]) {
      if (routes(status).length === 0) say('error', 'OL-IR-112', `Route ${status} from this fan-out: ${meaning} is a fact to act on, not a pass.`);
    }
    for (const to of routes('LIMIT_REACHED')) {
      const kind = byId.get(to)?.kind;
      if (g.onLimit === 'HUMAN_TASK' && kind && kind !== 'humanTask') say('error', 'OL-IR-113', 'With HUMAN_TASK, LIMIT_REACHED must route to a human task.');
      if (g.onLimit === 'FAIL' && kind && kind !== 'terminal') say('error', 'OL-IR-113', 'With FAIL, LIMIT_REACHED must route to an end step.');
    }
  }
  return out;
}

/**
 * A DELIBERATELY-PARTIAL, client-side diagnostics preview. This is NOT the production
 * static-analysis suite (that is the compiler, runs server-side, and is the artifact that signs —
 * spec §7.1). It surfaces a faithful subset for the read-only canvas so the status bar can show a
 * problems count. Codes reuse real compiler codes where the rule is identical; preview-only checks
 * use OL-PRE-*.
 */
export function previewDiagnostics(def: FlowDefinition): Diagnostic[] {
  const out: Diagnostic[] = [];
  const nodes = def.nodes ?? [];
  const edges = def.edges ?? [];
  if (nodes.length === 0) return out; // variant (delta-only) — nothing to analyse client-side.

  const byId = new Map(nodes.map((n) => [n.id, n]));
  const declaredStages = new Set(def.metadata.stages ?? []);

  // Dangling references (schema makes these unrepresentable; imports can still carry them).
  for (const e of edges) {
    if (!byId.has(e.from)) {
      out.push({ severity: 'error', code: 'OL-IR-DANGLING', edgeId: edgeId(e), message: `Edge source "${e.from}" is not a node.` });
    }
    if (!byId.has(e.to)) {
      out.push({ severity: 'error', code: 'OL-IR-DANGLING', edgeId: edgeId(e), message: `Edge target "${e.to}" is not a node.` });
    }
  }

  out.push(...fanOutDiagnostics(def));

  // Reserved AI kinds (compiler rejects with OL-IR-002; v1.1 exempts llmTask/confidenceGate).
  for (const n of nodes) {
    const meta = NODE_KIND_META[n.kind];
    if (meta?.reserved) {
      out.push({ severity: 'info', code: 'OL-IR-002', nodeId: n.id, message: `${meta.label} is a RESERVED AI primitive — the Wave-3 compiler rejects it.` });
    }
    if (declaredStages.size > 0 && !declaredStages.has(n.stage)) {
      out.push({ severity: 'info', code: 'OL-PRE-STAGE', nodeId: n.id, message: `Node stage ${n.stage} is not in metadata.stages.` });
    }
  }

  // Control-edge cycle detection (data edges excluded — they never gate, so a data back-edge is
  // legal). Mirrors OL-IR-020 in spirit.
  const cyc = detectControlCycle(nodes, edges);
  if (cyc.length > 0) {
    out.push({ severity: 'error', code: 'OL-IR-020', message: `Control-flow cycle through ${cyc.join(' -> ')}.` });
  }

  // Status-route exhaustiveness (spec §7.2 — warning + one-click fix in PS-2).
  for (const n of nodes) {
    if (n.kind !== 'decision' || !n.outcomes) continue;
    const routed = new Set(
      edges.filter((e) => e.from === n.id && e.kind === 'status' && e.onStatus).map((e) => e.onStatus),
    );
    const missing = n.outcomes.filter((o) => !routed.has(o));
    if (missing.length > 0) {
      out.push({ severity: 'warning', code: 'OL-PRE-EXHAUST', nodeId: n.id, message: `Decision "${n.id}" has unrouted outcome(s): ${missing.join(', ')}.` });
    }
  }

  return out;
}

/** Longest control-path cycle probe (Kahn residue). Returns a node cycle or []. */
function detectControlCycle(nodes: FlowNode[], edges: FlowEdge[]): string[] {
  const ids = new Set(nodes.map((n) => n.id));
  const adj = new Map<string, string[]>();
  const indeg = new Map<string, number>();
  ids.forEach((id) => {
    adj.set(id, []);
    indeg.set(id, 0);
  });
  for (const e of edges) {
    if (!isControlEdge(e) || !ids.has(e.from) || !ids.has(e.to)) continue;
    adj.get(e.from)!.push(e.to);
    indeg.set(e.to, (indeg.get(e.to) ?? 0) + 1);
  }
  const queue = [...ids].filter((id) => (indeg.get(id) ?? 0) === 0);
  let visited = 0;
  while (queue.length) {
    const n = queue.shift()!;
    visited++;
    for (const m of adj.get(n) ?? []) {
      indeg.set(m, (indeg.get(m) ?? 0) - 1);
      if ((indeg.get(m) ?? 0) === 0) queue.push(m);
    }
  }
  if (visited === ids.size) return [];
  // Residual nodes participate in ≥1 cycle; return them for the message.
  return [...ids].filter((id) => (indeg.get(id) ?? 0) > 0).slice(0, 6);
}
